import React from 'react';
import { useDispatch } from 'react-redux';
import { AppSettingsUpdatable } from '@/actions/updatable';
import {
    RemoveDefaultReviewAttachmentRequest,
    tryUpdateAppSettings,
    UploadDefaultReviewAttachmentRequest,
} from '@/actions/appSettings';
import { globalMessage } from '@/utils/globalMessage';
import { ReviewAttachmentUploadView } from '@/components/appSettings/FileUpload/ReviewAttachmentUploadView';
import { FileStats } from '@/types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface DefaultReviewAttachmentUploadProps {
    appId: string;
    orgId: string;
    stepConfigId?: string;
    currentlyUploadedReviewAttachment?: FileStats;
    onSetAttachmentFile?: () => void;
    onRemoveAttachmentFile?: () => void;
}

function DefaultReviewAttachmentUpload({
    appId,
    orgId,
    stepConfigId,
    currentlyUploadedReviewAttachment,
    onSetAttachmentFile,
    onRemoveAttachmentFile,
}: DefaultReviewAttachmentUploadProps) {
    const dispatch = useDispatch();

    //UPLOAD

    const uploadReviewAttachmentIsLoading = useIsLoading(AppSettingsUpdatable.UploadDefaultReviewAttachment);
    const uploadReviewAttachmentError = useError(AppSettingsUpdatable.UploadDefaultReviewAttachment);
    const uploadReviewAttachmentSuccess = useUpdated(AppSettingsUpdatable.UploadDefaultReviewAttachment);

    const handleUploadReviewAttachment = (file) => {
        const request: UploadDefaultReviewAttachmentRequest = {
            appId,
            orgId,
            file: file,
            stepConfigId,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UploadDefaultReviewAttachment, request));
    };

    React.useEffect(() => {
        if (uploadReviewAttachmentSuccess) {
            globalMessage.success('Default review attachment uploaded!');
            if (onSetAttachmentFile != undefined) onSetAttachmentFile();
        }
    }, [uploadReviewAttachmentSuccess]);

    React.useEffect(() => {
        if (uploadReviewAttachmentError) {
            globalMessage.error(uploadReviewAttachmentError);
        }
    }, [uploadReviewAttachmentError]);

    //DELETE

    const deleteReviewAttachmentIsLoading = useIsLoading(AppSettingsUpdatable.RemoveDefaultReviewAttachment);
    const deleteReviewAttachmentError = useError(AppSettingsUpdatable.RemoveDefaultReviewAttachment);
    const deleteReviewAttachmentSuccess = useUpdated(AppSettingsUpdatable.RemoveDefaultReviewAttachment);

    const removeReviewAttachment = (fileName) => {
        const request: RemoveDefaultReviewAttachmentRequest = {
            orgId,
            appId,
            fileName,
            stepConfigId,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RemoveDefaultReviewAttachment, request));
    };

    React.useEffect(() => {
        if (deleteReviewAttachmentSuccess) {
            globalMessage.success('Default review attachment removed!');
            if (onRemoveAttachmentFile != undefined) onRemoveAttachmentFile();
        }
    }, [deleteReviewAttachmentSuccess]);

    React.useEffect(() => {
        if (deleteReviewAttachmentError) {
            globalMessage.error(deleteReviewAttachmentError);
        }
    }, [deleteReviewAttachmentError]);

    return (
        <ReviewAttachmentUploadView
            description="Upload the file attachment to include in the review information for each release."
            attachment={currentlyUploadedReviewAttachment}
            isUploading={uploadReviewAttachmentIsLoading}
            isDeleting={deleteReviewAttachmentIsLoading}
            handleUpload={handleUploadReviewAttachment}
            handleDelete={removeReviewAttachment}
        />
    );
}

export default DefaultReviewAttachmentUpload;
